/**
 * Reimagined Notifications — drop-in replacement for tio-ui/components/notifications.
 *
 * ## Exports
 *
 * - `NotificationsContainer` — place once in your app layout template
 * - `NotificationsService` (type) — for `@service declare notifications`
 *
 * ## Usage
 *
 * ```gts
 * // application.gts
 * import { NotificationsContainer } from 'tio-ui/components/notifications';
 *
 * <template>
 *   {{outlet}}
 *   <NotificationsContainer />
 * </template>
 * ```
 *
 * ```ts
 * // In any component/route
 * import type { NotificationsService } from 'tio-ui/components/notifications';
 *
 * @service declare notifications: NotificationsService;
 *
 * this.notifications.add('Saved!', { appearance: 'success' });
 * ```
 */
import Component from '@glimmer/component';
import { service } from '@ember/service';
import ToastContainer from './toast';
import type NotificationsServiceClass from '../services/notifications.ts';

// Re-export the service type for consumers
export type NotificationsService = NotificationsServiceClass;

// ---------------------------------------------------------------------------
// NotificationsContainer — auto-wired to the notifications service
// ---------------------------------------------------------------------------

interface NotificationsContainerSignature {
  Args: {
    /**
     * Placement of the container. Currently always renders bottom-right
     * (reimagined design), this prop exists only for @frontile compat.
     */
    placement?: string;
  };
  Element: HTMLDivElement;
}

/**
 * Drop-in replacement for `<NotificationsContainer>` from tio-ui/notifications.
 *
 * Renders the reimagined Toast stack, auto-wired to the `notifications` service.
 * Place once in your application template.
 */
export class NotificationsContainer extends Component<NotificationsContainerSignature> {
  @service declare notifications: NotificationsServiceClass;

  dismiss = (id: string) => {
    this.notifications.dismiss(id);
  };

  <template>
    <ToastContainer
      @toasts={{this.notifications.toasts}}
      @onDismiss={{this.dismiss}}
      ...attributes
    />
  </template>
}

export default NotificationsContainer;
